import { FC, useCallback, useEffect } from 'react';

import { daysFromNow } from '@eneco/common/date';
import useDC from '@eneco/dc/useDC';
import { StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { I18nProvider, useFormatter, useTranslation } from '@eneco/i18n';
import { Checkbox, IconList, RadioGroup, Stack, Stretch, Text } from '@eneco/sparky';
import { ElectricityIcon, TruckIcon } from '@eneco/sparky/icons';
import { usePlaceholderContent } from '@sitecore/common';
import { NewDeliveryDateStepRendering } from '@sitecore/types/NewDeliveryDateStep';
import { Controller, useForm } from 'react-hook-form';

import { FlowConsumerContextEnergy } from '../../../../context';
import { useFlowNavigation } from '../../../../shared/hooks';
import { ASAPCardBody } from '../components/ASAPCardBody_B';
import { ContractEndCardBody } from '../components/ContractEndCardBody_B';
import { CustomDateCardBody } from '../components/CustomDateCardBody_B';
import { DeliveryDateOptionCard } from '../components/DeliveryDateOptionCard_B';
import content from '../content/DeliveryDateStep.nl-NL.json';
import { useDeliveryDateOptions_B } from '../hooks/useDeliveryDateOptions_B';
import { buildDeliveryDateOptionSchema_B, DeliveryDateFormFieldsB } from '../utils/deliveryDateOptionSchema_B';

/**
 * The step's own copy — the three option cards have no Sitecore fields, and `NewDeliveryDateStep.d.ts`
 * is generated from Sitecore, so the frontend cannot add them. Everything that *does* have a Sitecore
 * field (title, next-step text, all validation messages, the datepicker placeholder) still comes
 * from `textualData`.
 *
 * The dictionary is passed synchronously rather than as `locale => import(...)`: this flow is NL-only,
 * so the async loader would buy no extra locale while adding a loading state — and a blank frame — to
 * a step in the middle of the funnel. Adding a locale later means swapping this for the loader form.
 */
export const DeliveryDateStep: FC = () => (
  <I18nProvider dictionary={content}>
    <DeliveryDateStepContent />
  </I18nProvider>
);

/**
 * Delivery date step: three mutually exclusive `RadioCard` options, each explaining its own trade-off,
 * in place of a single free-form date field.
 *
 * The flow context still receives a single `deliveryDate` — derived from the selected option at
 * submit — so nothing downstream needs to know how it was chosen. `deliveryDateOption` is submitted
 * alongside it purely so back-navigation can restore the right card.
 */
const DeliveryDateStepContent: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();

  const {
    context: { isSME, isMoving, consentConsiderationPeriod, deliveryDate, deliveryDateOption },
  } = flowState;

  const { label } = useDC();
  const { format, date } = useFormatter();
  const { t } = useTranslation();

  const {
    minimumDays,
    minimumDate,
    maximumDate,
    asapDateValue,
    asapDateLabel,
    isWithinStartWindow,
    isWithinEndDateWindow,
    resolveDeliveryDate,
    isUrgentDeliveryDate,
    isEndDateBeforeEarliestStart,
    isConsentEligible,
    isWithinConsiderationPeriod,
  } = useDeliveryDateOptions_B({ isMoving, isSME, label });

  const { NewDeliveryDateStep: { fields: textualData } = {} } = usePlaceholderContent<{
    NewDeliveryDateStep: NewDeliveryDateStepRendering;
  }>();

  const customStartDateFallback = isWithinStartWindow(deliveryDate) ? deliveryDate : asapDateValue;

  const {
    handleSubmit,
    getValues,
    setValue,
    register,
    watch,
    control,
    formState: { errors, isSubmitSuccessful },
  } = useForm<DeliveryDateFormFieldsB>({
    mode: 'onSubmit',
    defaultValues: {
      deliveryDateOption,
      // A `CONTRACT_END` date is an *einddatum*: falling back to the earliest start date would
      // change its meaning, so a stale one is dropped and the customer re-enters it. Judged against
      // the end-date window, not the start window — an end date before the earliest start date is
      // valid here, and must not be thrown away on the way back into the step.
      contractEndDate:
        deliveryDateOption === 'CONTRACT_END' && isWithinEndDateWindow(deliveryDate) ? deliveryDate : '',
      // `CUSTOM` behaves like the control: a stale date falls back to the earliest start date.
      customStartDate: deliveryDateOption === 'CUSTOM' ? customStartDateFallback : '',
      consentConsiderationPeriod,
    },
    resolver: typedYupResolver<DeliveryDateFormFieldsB>(
      buildDeliveryDateOptionSchema_B({
        isMoving,
        minimumDate,
        maximumDate,
        minimumDays,
        groupRequiredMessage: t('groupRequiredMessage'),
        isConsentEligible,
        resolveDeliveryDate,
        isWithinConsiderationPeriod,
        format,
        dateShort: date.short,
        textualData,
      }),
    ),
  });

  const selectedOption = watch('deliveryDateOption');
  const contractEndDate = watch('contractEndDate');
  const customStartDate = watch('customStartDate');

  const derivedDeliveryDate = resolveDeliveryDate({
    deliveryDateOption: selectedOption,
    contractEndDate,
    customStartDate,
  });

  // Consent is given for one specific start date — the checkbox label spells that date out — so any
  // change to the date or the option means the customer has to agree again.
  const clearConsent = useCallback(() => setValue('consentConsiderationPeriod', false), [setValue]);

  const shouldShowExtraField = isConsentEligible && isWithinConsiderationPeriod(derivedDeliveryDate);

  useEffect(() => {
    if (!isSubmitSuccessful) return;

    const formFields = getValues();
    const submittedDeliveryDate = resolveDeliveryDate(formFields);

    updateContextAndGoToNextStep({
      deliveryDateOption: formFields.deliveryDateOption,
      deliveryDate: submittedDeliveryDate,
      consentConsiderationPeriod: formFields.consentConsiderationPeriod,
      isUrgent: isUrgentDeliveryDate(submittedDeliveryDate),
    });
  }, [isSubmitSuccessful]);

  /**
   * The variant has its own heading and drops the intro paragraph, neither of which the Sitecore
   * rendering can express: `content.title` still holds the control's wording and `content.text` still
   * holds the vaste-contract paragraph. `StepHeader` skips the paragraph when `text` is empty, so
   * overriding both fields keeps the header markup — and any Sitecore notification — while matching
   * the design. Promote these into real Sitecore fields if the variant wins.
   */
  const headerTextualData =
    textualData &&
    {
      ...textualData,
      content: { ...textualData.content, title: { value: t('title') }, text: { value: '' } },
    };

  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <StepHeader textualData={headerTextualData} />

      <Controller
        control={control}
        name="deliveryDateOption"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            label={t('groupLabel')}
            direction="column"
            alignY="start"
            wrap={false}
            isFullWidth
            value={value}
            onValueChange={option => {
              clearConsent();
              onChange(option);
            }}
            error={errors.deliveryDateOption?.message}>
            <DeliveryDateOptionCard
              option="ASAP"
              title={t('asap.title')}
              // Only this option's subtitle carries a date, so the emphasis is composed here rather
              // than in the shared card — the other two subtitles are plain text.
              subtitle={
                <>
                  <Text color="textLowEmphasis" weight="bold">
                    {asapDateLabel}{' '}
                  </Text>
                  {t('asap.subtitleAfterDate')}
                </>
              }
              badge={t('recommendedBadge')}
              isSelected={value === 'ASAP'}>
              <ASAPCardBody />
            </DeliveryDateOptionCard>

            <DeliveryDateOptionCard
              option="CONTRACT_END"
              title={t('contractEnd.title')}
              subtitle={t('contractEnd.subtitle')}
              isSelected={value === 'CONTRACT_END'}>
              <ContractEndCardBody
                control={control}
                errors={errors}
                // An end date may legitimately precede the earliest start date, so this picker opens
                // from today rather than from the switching minimum.
                fromDate={daysFromNow(0)}
                toDate={maximumDate}
                shouldShowEarlyEndDateNotification={isEndDateBeforeEarliestStart(contractEndDate)}
                onDayClick={clearConsent}
              />
            </DeliveryDateOptionCard>

            <DeliveryDateOptionCard
              option="CUSTOM"
              title={t('custom.title')}
              subtitle={t('custom.subtitle')}
              isSelected={value === 'CUSTOM'}>
              <CustomDateCardBody
                control={control}
                errors={errors}
                fromDate={minimumDate}
                toDate={maximumDate}
                onDayClick={clearConsent}
              />
            </DeliveryDateOptionCard>
          </RadioGroup>
        )}
      />

      {shouldShowExtraField && (
        <Checkbox
          {...register('consentConsiderationPeriod')}
          label={format(textualData?.deliveryDate?.isUrgentFormField?.value?.label ?? '', {
            startDate: derivedDeliveryDate ?? '',
          })}
          error={errors.consentConsiderationPeriod?.message}
        />
      )}

      <Stack.Item>
        <Stack direction="row" alignX="justify">
          <IconList.Item icon={<TruckIcon color="iconElectricityNew" />}>
            <Text size="BodyS">{t('usps.switch')}</Text>
          </IconList.Item>
          <IconList.Item icon={<ElectricityIcon color="iconElectricityNew" />}>
            <Text size="BodyS">{t('usps.alwaysEnergy')}</Text>
          </IconList.Item>
        </Stack>
      </Stack.Item>

      <Stretch width="auto">
        <NextButton size="regular" isFullWidth isLoading={isSubmitSuccessful}>
          {textualData?.content?.nextStepText?.value}
        </NextButton>
      </Stretch>
    </Layout.Content>
  );
};
